import { createEffect, createEvent, createStore, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { $webEditor } from '@nx-frontend/shared/store/web-editor-store';
import { EditorInstance, ImageWrapperBlot } from '@nx-frontend/shared/types';
import { getCookieValue } from '@nx-frontend/shared/utils';
import { webnotesClientEnv } from '@nx-frontend/webnotes-client/shared';

const SERVER_URL = webnotesClientEnv.guideGenUrl;

interface GuideGenAttachment {
  workspaceId: string;
  globalId: string;
  url: string;
  displayName: string;
  duration: number;
  videoElement: HTMLVideoElement;
}

interface GuideGenStore {
  active: boolean;
  attachment?: GuideGenAttachment;
  generating: boolean;
  videoDescription: string;
  steps?: Step[];
  /**
   * Video summary generated on the server
   */
  videoSummary?: string;
  error: string;
}

interface StartGuidGenData {
  videoDescription: string;
}

interface Step {
  /**
   * time code for a frame, like '01:20' or '01:20:30'
   */
  frame: string;
  /**
   * heading text for the frame
   */
  heading: string;
  /**
   * paragraphs of text for the frame
   */
  paragraphs: string[];
}

export const $guideGen = createStore<GuideGenStore>({
  active: false,
  attachment: undefined,
  generating: false,
  videoDescription: '',
  videoSummary: '',
  steps: undefined,
  error: '',
});

export const activateGuideGenEvent = createEvent<GuideGenAttachment>();
export const startGuidGenEvent = createEvent<StartGuidGenData>();

const generateGuideFx = createEffect(async ({ store }: { store: GuideGenStore }) => {
  const res = await axiosClient.post(
    SERVER_URL,
    {
      workspaceId: store.attachment?.workspaceId,
      attachmentGlobalId: store.attachment?.globalId,
      videoDescription: store.videoDescription,
    },
    {
      headers: {
        'Everhelper-Session-Id': getCookieValue('eversessionid'),
      },
    }
  );

  return res.data.response;
});

/**
 *
 * @param video
 * @param time has format 'hh:mm:ss' or just 'mm:ss'
 * @param delta adds this value in seconds for final seeking, it is to seek more precisely to actual frame with information
 * @returns
 */
async function getVideoFrameAsBlob(video: HTMLVideoElement, time: string, delta = 0) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // convert time to seconds
    const parts = time.split(':');
    let seconds = 0;
    if (parts.length === 3) {
      seconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
    } else if (parts.length === 2) {
      seconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
    } else {
      reject('Invalid time format');
    }

    // wait until video is ready
    const seekCallback = () => {
      context?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        resolve(blob);
      });
      video.removeEventListener('seeked', seekCallback);
    };
    video.addEventListener('seeked', seekCallback);
    video.currentTime = seconds + delta;
  });
}

const renderGuideFx = createEffect(async ({ editor, store }: { editor: EditorInstance; store: GuideGenStore }) => {
  const steps = store.steps || [];

  console.log('Rendering guide FX', store.steps, steps, store.attachment?.videoElement);

  if (store.videoDescription) {
    editor.scroll.addBlot('paragraph', {
      collapsed: false,
      delta: {
        ops: [
          {
            insert: store.videoSummary,
          },
        ],
      },
    });
  }

  for (const step of steps) {
    if (!store.attachment) {
      continue;
    }
    const stepImage = await getVideoFrameAsBlob(store.attachment?.videoElement, step.frame, 1);
    try {
      const items = [];
      items.push(
        ...step.paragraphs.map((paragraph) => {
          return {
            type: 'paragraph',
            initParams: {
              delta: { ops: [{ insert: paragraph }] },
            },
          };
        })
      );
      items.push({
        type: 'image',
      });
      const { id: stepBlotId } = editor.scroll.addBlot('step', {
        collapsed: false,
        delta: {
          ops: [
            {
              insert: step.heading,
            },
          ],
        },
        items,
      });

      const stepBlot = editor.scroll.getBlotById(stepBlotId);

      const imageWrapperBlot = stepBlot?.children?.tail?.children.tail as ImageWrapperBlot;
      const imageBlot = imageWrapperBlot.image;
      imageBlot.setHTMLFileForUpload({
        instance: stepImage as File,
      });

      console.log('ADDED', stepBlotId, step, stepBlot);
    } catch (err) {
      console.error('FAILED', err);
    }
  }
});

$guideGen.on(activateGuideGenEvent, (store, attachment) => ({ ...store, attachment, active: true }));

$guideGen.on(startGuidGenEvent, (store, data) => ({
  ...store,
  videoDescription: data.videoDescription,
  generating: true,
}));

$guideGen.on(generateGuideFx.done, (store, { result }) => {
  return {
    ...store,
    steps: result.steps,
    videoSummary: result.description,
  };
});

$guideGen.on(renderGuideFx.done, (store, { result }) => {
  return {
    ...store,
    generating: false,
    active: false,
  };
});

sample({
  clock: startGuidGenEvent,
  source: $guideGen,
  target: generateGuideFx,
  fn: (store) => ({ store }),
});

sample({
  clock: generateGuideFx.done,
  source: [$webEditor, $guideGen],
  fn: ([editor, store]) => ({ editor: editor as EditorInstance, store: store as GuideGenStore }),
  target: renderGuideFx,
});
